import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as d3 from 'd3'

import { AppLayoutContext } from '../../../providers/AppLayout'
import { useUrlStateParams } from '../../../utils/url'
import { SnackbarContext } from '../../../providers/SnackbarContext'

import { Agency, agencyFields } from '../../../models/Agency.model'
import { AgencyAddEvent } from '../../../events/AgencyAddEvent'
import { AgencyUpdateEvent } from '../../../events/AgencyUpdateEvent'

import { AgencyService } from '../../../services/AgencyService/AgencyService'

import {
  Box,
  FormControl,
  OutlinedInput,
  InputLabel,
  MenuItem,
  Stack,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SearchIcon from '@mui/icons-material/Search'
import DataTable from '../../../components/DataTable/DataTable.component'
import Loading from '../../../components/Loading/Loading.component'
import ImportExportDialog, {
  ImportExportDialogProps,
} from '../../../components/ImportExportDialog/ImportExportDialog.component'
import { Link } from 'react-router-dom'
import MuiLink from '@mui/material/Link'
import AgencyFormDialog from '../shared/AgencyFormDialog.component'
import StarRating from '../../../components/StarRating/StarRating.component'
import AgencyIcon from '../../../icons/AgencyIcon'

const AgencySearch = (): JSX.Element => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [searchError, setSearchError] = useState('')
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<null | HTMLElement>(null)
  const isActionsMenuOpen = Boolean(rowActionsAnchorEl)
  const [selectedRow, setSelectedRow] = useState<Agency | undefined>()
  const [isAdding, setIsAdding] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const [term, setTerm] = useUrlStateParams(
    '',
    'term',
    (value) => encodeURIComponent(value.toString()),
    (value) => (value ? decodeURIComponent(value) : ''),
  )
  const termInputRef = useRef<HTMLInputElement>()
  const [orderBy, setOrderBy] = useState<keyof Agency | undefined>('name')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc' | undefined>('asc')
  const [page, setPage] = useUrlStateParams(
    1,
    'page',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 1),
  )
  const [pageSize, setPageSize] = useUrlStateParams(
    12,
    'page-size',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 12),
  )

  const [agencies, setAgencies] = useState<Agency[]>([])
  const [totalResults, setTotalResults] = useState(0)

  const [isImportExportOpen, setIsImportExportOpen] = useState(false)

  const agencyService = useMemo(() => new AgencyService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName('Agencies')
  }, [])

  useEffect(() => {
    setIsLoading(true)
    setSearchError('')
    agencyService
      .search({ page, pageSize, term, orderBy, orderDirection })
      .then((results) => {
        setIsLoading(false)
        setAgencies(results.agencies)
        setTotalResults(results.totalResults)
        setPage(results.currentPage)
      })
      .catch((err) => {
        setSearchError(err)
        console.error(err)
        setIsLoading(false)
      })
  }, [term, page, pageSize, orderBy, orderDirection])

  const handleRowActionsClick = (event: React.MouseEvent<HTMLButtonElement>, agency: Agency) => {
    setRowActionsAnchorEl(event.currentTarget)
    setSelectedRow(agency)
  }
  const handleRowActionsClose = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
  }

  const columns = useMemo(() => {
    const columns: GridColDef<Agency>[] = [
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButton
              id={`row-actions-button-${params.row.id}`}
              color="primary"
              aria-label="row actions"
              aria-controls={selectedRow?.id === params.row.id ? 'row-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={selectedRow?.id === params.row.id ? 'true' : undefined}
              onClick={(event) => handleRowActionsClick(event, params.row)}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        ),
      },
      {
        field: 'imageURL',
        headerName: 'Image',
        width: 100,
        sortable: false,
        renderCell: (params) =>
          params.row.imageURL ? (
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.50',
              }}
            >
              <img
                src={params.row.imageURL}
                alt={params.row.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 1,
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <AgencyIcon sx={{ fontSize: 28, color: 'text.secondary', opacity: 0.5 }} />
            </Box>
          ),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 100,
        renderCell: (params) => (
          <Typography variant="body2" color="text.primary">
            {params.row.id}
          </Typography>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 350,
        renderCell: (params) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'primary.main' }}>
              {params.row.name}
            </Typography>
            {params.row.address && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {params.row.address}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'averageRating',
        headerName: 'Rating',
        width: 150,
        renderCell: (params) => (
          <Box>
            {params.row.averageRating !== undefined && params.row.averageRating !== null ? (
              <>
                <StarRating rating={Number(params.row.averageRating) || 0} size="small" />
                {params.row.reviewCount !== undefined && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {params.row.reviewCount} {params.row.reviewCount === 1 ? 'review' : 'reviews'}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'phone',
        headerName: 'Phone',
        width: 150,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.row.phone && params.row.phone !== 'NOT AVAILABLE' ? params.row.phone : '-'}
          </Typography>
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 200,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.row.email || '-'}
          </Typography>
        ),
      },
      {
        field: 'website',
        headerName: 'Website',
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Box>
            {params.row.website ? (
              <Tooltip title={params.row.website}>
                <IconButton
                  href={params.row.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ marginLeft: 1 }}>
                -
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'facebook',
        headerName: 'Facebook',
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Box>
            {params.row.facebook ? (
              <Tooltip title={params.row.facebook}>
                <IconButton
                  href={params.row.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ marginLeft: 1 }}>
                -
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'permalink',
        headerName: 'Permalink',
        width: 350,
        renderCell: (params) => (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%',
            }}
          >
            {params.row.permalink || '-'}
          </Typography>
        ),
      },
      {
        field: 'externalId',
        headerName: 'External ID',
        width: 120,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.row.externalId || '-'}
          </Typography>
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        width: 180,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.row.createdAt ? new Date(params.row.createdAt).toLocaleString() : '-'}
          </Typography>
        ),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        width: 180,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.row.updatedAt ? new Date(params.row.updatedAt).toLocaleString() : '-'}
          </Typography>
        ),
      },
    ]
    return columns
  }, [selectedRow])

  const handleSubmit = () => {
    setPage(1)
    setTerm(termInputRef?.current?.value || '')
  }

  const handleAddClick = () => {
    setIsAdding(true)
  }

  const handleAddCancel = () => {
    setIsAdding(false)
  }

  const handleAddSubmit = (
    newAgency: AgencyAddEvent,
    setIsAddFormSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (!Object.keys(newAgency).length) {
      // nothing changed so just close the dialog
      setIsAdding(false)
    } else {
      setIsLoading(true)
      agencyService
        .add(newAgency)
        .then(() => {
          SnackbarContext.show('Agency added successfully!')

          setIsAdding(false)

          // fetch new data
          setSearchError('')
          agencyService
            .search({ pageSize, term, orderBy, orderDirection })
            .then((results) => {
              setAgencies(results.agencies)
              setTotalResults(results.totalResults)
              setPage(1)
              setIsLoading(false)
            })
            .catch((err) => {
              setSearchError(err)
              console.error(err)
              setIsLoading(false)
            })
        })
        .catch((err) => {
          SnackbarContext.show(`Agency failed to add: ${err?.data?.message || err?.message || err}`, 'error')
          console.error(err)
          setIsLoading(false)
          setIsAddFormSubmitting(false)
        })
    }
  }

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleEditCancel = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
    setIsEditing(false)
  }

  const handleEditSubmit = (
    changedProperties: AgencyUpdateEvent,
    setIsEditFormSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (!Object.keys(changedProperties).length) {
      // nothing changed so just close the dialog
      setRowActionsAnchorEl(null)
      setSelectedRow(undefined)
      setIsEditing(false)
    } else if (selectedRow) {
      setIsLoading(true)
      agencyService
        .update(Number(selectedRow.id), changedProperties)
        .then((result) => {
          SnackbarContext.show('Agency updated successfully!')
          setRowActionsAnchorEl(null)
          setSelectedRow(undefined)
          // replace the agency in the list with the updated one
          setAgencies((prevAgencies) => {
            const index = prevAgencies.findIndex((agency) => agency.id === result.id)
            if (index > -1) {
              const newAgencies = [...prevAgencies]
              newAgencies[index] = result
              return newAgencies
            }
            return prevAgencies
          })
          setIsLoading(false)
          setIsEditing(false)
        })
        .catch((err) => {
          SnackbarContext.show(`Agency failed to edit: ${err?.data?.message || err?.message || err}`, 'error')
          console.error(err)
          setIsLoading(false)
          setIsEditFormSubmitting(false)
        })
    }
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false)
    handleRowActionsClose()
  }

  const handleDelete = () => {
    if (selectedRow?.id) {
      setIsLoading(true)
      setIsDeleteConfirmationOpen(false)
      agencyService
        .delete(selectedRow.id)
        .then(() => {
          SnackbarContext.show('Agency deleted successfully!')
          setRowActionsAnchorEl(null)
          setSelectedRow(undefined)
          setAgencies(agencies.filter((agency) => agency.id !== selectedRow?.id))
          setTotalResults(totalResults - 1)
          setIsLoading(false)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Agency failed to delete: ${err?.data?.message || err?.message || err}`, 'error')
          setIsLoading(false)
        })
    }
  }

  const openImportExport = () => {
    setIsImportExportOpen(true)
  }

  const handleImportExportClose = () => {
    setIsImportExportOpen(false)
  }

  const handleImport: ImportExportDialogProps['onImport'] = (data: Partial<Agency>[], options) => {
    setIsImportExportOpen(false)
    if (!data) {
      SnackbarContext.show('Failed to read/parse file', 'error')
      console.error('Failed to read/parse file')
    } else {
      setIsLoading(true)
      agencyService
        .import(data, options)
        .then(() => {
          SnackbarContext.show('Agencies imported successfully!')

          // fetch new data
          setSearchError('')
          agencyService
            .search({ pageSize, term, orderBy, orderDirection })
            .then((results) => {
              setAgencies(results.agencies)
              setTotalResults(results.totalResults)
              setPage(1)
              setIsLoading(false)
            })
            .catch((err) => {
              setSearchError(err)
              console.error(err)
              setIsLoading(false)
            })
        })
        .catch((err) => {
          SnackbarContext.show(`Agency failed to import: ${err?.data?.message || err?.message || err}`, 'error')
          console.error(err)
          setIsLoading(false)
        })
    }
  }

  const handleExport = () => {
    // do a request to the API to get the data to export with no pagination
    setIsLoading(true)
    setIsImportExportOpen(false)
    agencyService
      .search({ page: 1, pageSize: 999999, term, orderBy, orderDirection })
      .then((results) => {
        if (!results.agencies || results.agencies.length === 0) {
          SnackbarContext.show('No agencies found to export', 'warning')
          setIsLoading(false)
          return
        }

        const exportData = results.agencies.map((agency) => ({
          id: agency.id,
          name: agency.name,
          imageURL: agency.imageURL || '',
          permalink: agency.permalink,
          address: agency.address || '',
          website: agency.website || '',
          phone: agency.phone || '',
          email: agency.email || '',
          facebook: agency.facebook || '',
          externalId: agency.externalId || '',
          averageRating: String(agency.averageRating),
          reviewCount: String(agency.reviewCount || 0),
          updatedAt: agency.updatedAt,
          createdAt: agency.createdAt,
        }))

        if (exportData.length === 0) {
          SnackbarContext.show('No data to export', 'warning')
          setIsLoading(false)
          return
        }

        const csvData = d3.csvFormat(exportData)

        if (!csvData || csvData.trim().length === 0) {
          SnackbarContext.show('Failed to generate CSV data', 'error')
          setIsLoading(false)
          return
        }

        // initiate a download of the csv file
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.setAttribute('href', url)
        link.setAttribute('download', 'agencies.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        SnackbarContext.show(`Exported ${exportData.length} agencies successfully!`)
        setIsLoading(false)
      })
      .catch((err) => {
        SnackbarContext.show(
          `Agency failed to fetch agencies for export: ${err?.data?.message || err?.message || err}`,
          'error',
        )
        console.error(err)
        setIsLoading(false)
      })
  }

  return (
    <>
      {selectedRow && (
        <AgencyFormDialog
          open={isEditing}
          agency={selectedRow}
          onEdit={handleEditSubmit}
          onCancel={handleEditCancel}
          onClose={handleEditCancel}
        />
      )}
      <AgencyFormDialog open={isAdding} onAdd={handleAddSubmit} onCancel={handleAddCancel} onClose={handleAddCancel} />
      <Dialog fullWidth maxWidth="xs" open={isDeleteConfirmationOpen} onClose={handleDeleteConfirmationClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          Are you sure you want to delete agency id {selectedRow?.id}: {selectedRow?.name}?
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleDeleteConfirmationClose}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <ImportExportDialog
        open={isImportExportOpen}
        exampleImportFileUrl="/example-import-files/agencies.csv"
        importFields={agencyFields.filter((field) => !['updatedAt', 'createdAt'].includes(field))}
        onImport={handleImport}
        onExport={handleExport}
        onClose={handleImportExportClose}
      />
      <Paper sx={{ p: { xxxs: 1, xxs: 1.25, xs: 1.5, sm: 2 } }}>
        {isLoading && !searchError && <Loading sx={{ py: 20 }} />}
        {searchError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{searchError}</Typography>
          </Stack>
        )}
        {!isLoading && !searchError && (
          <>
            <Box sx={{ mb: { xxxs: 1, xxs: 1.25, xs: 1.5, sm: 2 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xxxs: 1, xxs: 1.25, xs: 1.5, sm: 2 }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{ width: '100%' }}
              >
                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    flex: { md: '1 1 250px' },
                    minWidth: { md: 200 },
                    maxWidth: { md: 'none' },
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xxxs: '0.875rem', xs: '0.9375rem' },
                      height: { xxxs: '36px', xs: '40px' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xxxs: '0.875rem', xs: '0.9375rem' },
                    },
                  }}
                >
                  <InputLabel>Agency Name</InputLabel>
                  <OutlinedInput
                    id="search-term"
                    label="Agency Name"
                    defaultValue={term || ''}
                    inputRef={termInputRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit()
                      }
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{
                            fontSize: { xxxs: '18px', xs: '20px' },
                          }}
                          color="action"
                        />
                      </InputAdornment>
                    }
                    endAdornment={
                      term ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear search term"
                            onClick={() => {
                              if (termInputRef?.current?.value) {
                                termInputRef.current.value = ''
                              }
                              setPage(1)
                              setTerm('')
                            }}
                            edge="end"
                            size="small"
                            sx={{
                              padding: { xxxs: '4px', xs: '8px' },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined
                    }
                  />
                </FormControl>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xxxs: 1, xxs: 1.25, xs: 1.5, sm: 1.5 }}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    flex: { md: '0 0 auto' },
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSubmit}
                    startIcon={<SearchIcon />}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { xs: 'auto', sm: 95 },
                      height: { xxxs: '36px', xs: '40px' },
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: { xxxs: '0.8125rem', xs: '0.875rem' },
                      px: { xxxs: 1.5, xs: 2 },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Search
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { xs: 'auto', sm: 110 },
                      height: { xxxs: '36px', xs: '40px' },
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: { xxxs: '0.8125rem', xs: '0.875rem' },
                      px: { xxxs: 1.5, xs: 2 },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Add
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ImportExportIcon />}
                    onClick={openImportExport}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { xs: 'auto', sm: 135 },
                      height: { xxxs: '36px', xs: '40px' },
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: { xxxs: '0.75rem', xxs: '0.8125rem', xs: '0.875rem' },
                      px: { xxxs: 1.5, xxs: 1.5, xs: 2 },
                      whiteSpace: 'nowrap',
                      '& .MuiButton-startIcon': {
                        marginRight: { xxxs: 0.5, xs: 1 },
                      },
                    }}
                  >
                    Import/Export
                  </Button>
                </Stack>
              </Stack>

              {/* Active Filters Display */}
              {term && (
                <Box sx={{ mt: { xxxs: 1, xxs: 1.25, xs: 1.5, sm: 2 } }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ gap: { xxxs: 0.5, xxs: 0.75, xs: 0.75, sm: 1 } }}
                  >
                    <Chip
                      label={
                        <Box
                          component="span"
                          sx={{
                            maxWidth: { xxxs: '120px', xxs: '150px', xs: '200px', sm: 'none' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                        >
                          {`Name: ${term}`}
                        </Box>
                      }
                      onDelete={() => {
                        if (termInputRef?.current?.value) {
                          termInputRef.current.value = ''
                        }
                        setPage(1)
                        setTerm('')
                      }}
                      color="primary"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xxxs: '0.6875rem', xxs: '0.75rem', xs: '0.75rem', sm: '0.8125rem' },
                        height: { xxxs: 24, xxs: 26, xs: 28, sm: 32 },
                        '& .MuiChip-label': {
                          px: { xxxs: 0.75, xxs: 1, xs: 1, sm: 1.25 },
                        },
                      }}
                    />
                  </Stack>
                </Box>
              )}
            </Box>

            {agencies.length > 0 ? (
              <>
                <Menu
                  id="row-actions-menu"
                  anchorEl={rowActionsAnchorEl}
                  open={isActionsMenuOpen}
                  onClose={handleRowActionsClose}
                  MenuListProps={{
                    'aria-labelledby': `row-actions-button${selectedRow?.id}`,
                  }}
                >
                  <MenuItem onClick={() => navigate(`/agencies/${selectedRow?.id}`)}>View</MenuItem>
                  <MenuItem onClick={handleEditClick}>Edit</MenuItem>
                  <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
                </Menu>
                <DataTable
                  columns={columns}
                  data={agencies}
                  total={totalResults}
                  paginationMode="server"
                  page={page}
                  pageSize={pageSize}
                  onPageChange={(newPage) => {
                    setPage(newPage)
                  }}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize)
                  }}
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  onOrderChange={(newOrder) => {
                    setOrderBy(newOrder.field ? (newOrder.field as keyof Agency) : undefined)
                    setOrderDirection(newOrder.sort)
                  }}
                  onRowClick={(row) => {
                    navigate(`/agencies/${row.id}`)
                  }}
                />
              </>
            ) : (
              <Box
                sx={{
                  p: { xxxs: 2, xxs: 3, xs: 4, sm: 6, md: 8 },
                  textAlign: 'center',
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: '2px dashed',
                  borderColor: 'divider',
                }}
              >
                <AgencyIcon
                  sx={{
                    fontSize: { xxxs: 40, xxs: 44, xs: 48, sm: 56, md: 64 },
                    color: 'text.secondary',
                    mb: { xxxs: 1, xxs: 1.25, xs: 1.5, sm: 2 },
                    opacity: 0.5,
                  }}
                />
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{
                    mb: { xxxs: 0.75, xs: 1 },
                    fontWeight: 600,
                    fontSize: { xxxs: '0.9375rem', xxs: '1rem', xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  No agencies found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xxxs: '0.8125rem', xxs: '0.875rem', xs: '0.875rem', sm: '0.9375rem' },
                    px: { xxxs: 1, xxs: 1.5, xs: 2, sm: 0 },
                  }}
                >
                  {term
                    ? 'Try adjusting your search filters to find more results.'
                    : 'Get started by adding a new agency or importing from a file.'}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default AgencySearch
